import {
  ChevronFirstIcon,
  ChevronLastIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreatorsTableContext } from "./CreatorsTableContext";
import { TablePaginationProps } from "./types";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";

const NAV_BUTTON_CLASS =
  "size-7 hover:bg-muted-foreground/10 disabled:pointer-events-none disabled:opacity-50";

/**
 * Frame-footer pagination row: range + page-size select on the left, the
 * page indicator between first/prev and next/last on the right. Everything
 * ghost — it sits directly on the gray frame.
 */
export default function TablePagination({ id }: TablePaginationProps) {
  const { table } = useCreatorsTableContext();

  const { pageIndex, pageSize } = table.getState().pagination;
  const rowCount = table.getRowCount();
  const pageCount = Math.max(table.getPageCount(), 1);
  const rangeStart = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeEnd = Math.min((pageIndex + 1) * pageSize, rowCount);

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <p className="whitespace-nowrap tabular-nums" aria-live="polite">
          <span className="text-foreground">
            {rangeStart}-{rangeEnd}
          </span>{" "}
          of <span className="text-foreground">{rowCount}</span>
        </p>
        <Label htmlFor={id} className="max-sm:sr-only text-xs font-normal text-muted-foreground">
          Results per page
        </Label>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => {
            table.setPageSize(Number(value));
          }}
        >
          <ScopeWrapper>
            <SelectTrigger id={id} variant="ghost" className="h-7 gap-1 px-2 text-xs">
              <SelectValue placeholder="Select number of results" />
            </SelectTrigger>
          </ScopeWrapper>
          <SelectContent>
            {[5, 10, 25, 50, 100].map((size) => (
              <SelectItem key={size} value={size.toString()} className="h-7.5">
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Pagination className="mx-0 w-auto justify-end">
        <PaginationContent className="gap-0.5">
          <PaginationItem>
            <Button
              size="icon"
              variant="ghost"
              className={NAV_BUTTON_CLASS}
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Go to first page"
            >
              <ChevronFirstIcon size={16} aria-hidden="true" />
            </Button>
          </PaginationItem>
          <PaginationItem>
            <Button
              size="icon"
              variant="ghost"
              className={NAV_BUTTON_CLASS}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Go to previous page"
            >
              <ChevronLeftIcon size={16} aria-hidden="true" />
            </Button>
          </PaginationItem>
          <PaginationItem>
            <p className="px-1.5 whitespace-nowrap tabular-nums" aria-live="polite">
              <span className="text-foreground">{pageIndex + 1}</span> of {pageCount}
            </p>
          </PaginationItem>
          <PaginationItem>
            <Button
              size="icon"
              variant="ghost"
              className={NAV_BUTTON_CLASS}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Go to next page"
            >
              <ChevronRightIcon size={16} aria-hidden="true" />
            </Button>
          </PaginationItem>
          <PaginationItem>
            <Button
              size="icon"
              variant="ghost"
              className={NAV_BUTTON_CLASS}
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Go to last page"
            >
              <ChevronLastIcon size={16} aria-hidden="true" />
            </Button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
